import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { SypacButton, SypacText } from '@sypac/component-library-react';
import { T, useTranslate } from '@tolgee/react';
import { Sorting } from '../../../components/Sorting/Sorting';
import { SortingItemInterface } from '../../../components/Sorting/Sorting.interface';
import { cn } from '../../../lib/utils';
import {
	PricingMode,
	TransportConfig,
	TransportConfigService,
} from '../../../services/transport-config.services';
import {
	CATEGORY_NAMES,
	FormValues,
	NUMBER_MAX_DECIMALS,
	PRICING_MODES,
	buildTransportConfigPayload,
	createInitialValues,
} from './transportFormula.utils';
import FormInput from './components/FormInput';
import CategoryRow from './components/CategoryRow';
import ZoneHeader from './components/ZoneHeader';
import CostPreview from './components/CostPreview';
import { toastVariant } from '../../../components/ToastVariant/toastVariant';

/**
 * Admin form for transport pricing v3 ("Kalkulator 3",
 * docs/transport-pricing-kalkulator3-spec.md §2.4 / §4.7).
 *
 * Zones are data-driven (ids and count come from the config, sorted by
 * `from`). Each zone has a pricing mode that defines what its matrix cells
 * mean: DAILY_RATE → daily truck rate, PER_TON_KM → price per ton per km.
 */

const SkeletonInput: React.FC<{ className?: string }> = ({
	className = '',
}) => (
	<div
		className={cn(
			'w-full h-[42px] animate-pulse bg-gray-10/50 rounded-[8px]',
			className,
		)}
	/>
);

/** Message from a Nest 400 ({ message: string | string[] }) or a fallback. */
const apiErrorMessage = (error: any, fallback: string) => {
	const message = error?.response?.data?.message;
	if (Array.isArray(message)) return message.join('; ');
	return typeof message === 'string' && message ? message : fallback;
};

const positiveNumber = (label: string) =>
	Yup.string()
		.required(`${label} is required`)
		.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number')
		.test('positive', `${label} must be greater than 0`, (v) =>
			v ? parseFloat(v) > 0 : false,
		);

const TransportationFormula: React.FC = () => {
	const { t } = useTranslate();
	const [editFormula, setEditFormula] = useState<boolean>(false);
	const sortOptions = [
		{ title: t('adminProducts.poland', 'Poland'), value: 'PL' },
		{ title: t('adminProducts.moldova', 'Moldova'), value: 'MD' },
	];
	const [sorting, setSorting] = useState<SortingItemInterface>(sortOptions[0]);
	const [configData, setConfigData] = useState<TransportConfig | undefined>(
		undefined,
	);
	const [loading, setLoading] = useState<boolean>(false);
	const currency = sorting.value === 'PL' ? 'PLN' : 'MDL';

	const getTransportationConfig = useCallback(async (country: 'PL' | 'MD') => {
		setLoading(true);
		try {
			const { data } = await TransportConfigService.getTransportConfig(country);
			setConfigData(data);
		} catch (error) {
			console.error('Failed to fetch transportation config:', error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		getTransportationConfig(sorting.value as 'PL' | 'MD').then(() => {});
	}, [getTransportationConfig, sorting.value]);

	const formik = useFormik<FormValues>({
		enableReinitialize: true,
		initialValues: createInitialValues(configData),
		onSubmit: async (values) => {
			try {
				await TransportConfigService.updateTransportConfig(
					buildTransportConfigPayload(values, sorting.value as 'PL' | 'MD'),
				);
				toastVariant(
					t(
						'transportationFormula.transportationFormulaUpdated',
						'Transportation Formula updated',
					),
					false,
				);
				setEditFormula(false);
				await getTransportationConfig(sorting.value as 'PL' | 'MD');
			} catch (error) {
				console.error('Failed to update transport config:', error);
				toastVariant(
					apiErrorMessage(
						error,
						t(
							'transportationFormula.updateFailed',
							'Transportation formula could not be saved',
						),
					),
					true,
				);
			}
		},
		validationSchema: Yup.object({
			margin: Yup.string()
				.required('Margin is required')
				.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number')
				.test('max', 'Margin cannot exceed 100', (v) =>
					v ? parseFloat(v) <= 100 : true,
				),
			workingHoursPerDay: positiveNumber('Working hours').test(
				'max24',
				'Working hours cannot exceed 24',
				(v) => (v ? parseFloat(v) <= 24 : true),
			),
			averageSpeedKmh: positiveNumber('Average speed'),
			categories: Yup.object({
				category1: Yup.object({
					from: Yup.string()
						.required('From value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
					to: Yup.string()
						.required('To value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
				}),
				category2: Yup.object({
					from: Yup.string()
						.required('From value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
					to: Yup.string()
						.required('To value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
				}),
				category3: Yup.object({
					from: Yup.string()
						.required('From value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
					to: Yup.string()
						.required('To value is required')
						.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
				}),
			}).test(
				'category-validations',
				'Category validation',
				function (categories) {
					if (!categories) return true;
					const errors: Yup.ValidationError[] = [];
					CATEGORY_NAMES.forEach((catKey, index) => {
						const category = categories[catKey];
						const fromVal = parseFloat(category?.from || '');
						const toVal = parseFloat(category?.to || '');
						if (!isNaN(fromVal) && !isNaN(toVal) && fromVal > toVal) {
							errors.push(
								this.createError({
									path: `categories.${catKey}.from`,
									message: `Category ${index + 1} "from" must be ≤ "to"`,
								}),
							);
						}
					});
					for (let i = 0; i < CATEGORY_NAMES.length - 1; i++) {
						const toVal = parseFloat(categories[CATEGORY_NAMES[i]]?.to || '');
						const nextFrom = parseFloat(
							categories[CATEGORY_NAMES[i + 1]]?.from || '',
						);
						if (!isNaN(toVal) && !isNaN(nextFrom) && toVal >= nextFrom) {
							errors.push(
								this.createError({
									path: `categories.${CATEGORY_NAMES[i]}.to`,
									message: `Category ${i + 1} "to" must be < Category ${
										i + 2
									} "from"`,
								}),
							);
						}
					}
					if (errors.length > 0) throw new Yup.ValidationError(errors);
					return true;
				},
			),
			zones: Yup.object({
				ids: Yup.array().of(Yup.string()),
				distances: Yup.array()
					.of(
						Yup.object({
							from: Yup.string()
								.required('Distance from is required')
								.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number'),
							to: Yup.string()
								.matches(NUMBER_MAX_DECIMALS, 'Must be a valid number')
								.notRequired(),
						}),
					)
					.test('zone-validations', 'Zone validation', function (distances) {
						if (!distances) return true;
						const errors: Yup.ValidationError[] = [];
						const last = distances.length - 1;
						distances.forEach((zone, i) => {
							const fromVal = parseFloat(zone.from || '');
							const toVal = parseFloat(zone.to || '');
							const isOpen = !zone.to;
							if (isOpen && i !== last) {
								errors.push(
									this.createError({
										path: `zones.distances[${i}].to`,
										message: 'Only the last zone can be open-ended',
									}),
								);
							}
							if (!isOpen && i === last) {
								errors.push(
									this.createError({
										path: `zones.distances[${i}].to`,
										message: 'The last zone must be open-ended (leave empty)',
									}),
								);
							}
							if (
								!isOpen &&
								!isNaN(fromVal) &&
								!isNaN(toVal) &&
								fromVal > toVal
							) {
								errors.push(
									this.createError({
										path: `zones.distances[${i}].from`,
										message: '"from" must be ≤ "to"',
									}),
								);
							}
							if (i < last) {
								const nextFrom = parseFloat(distances[i + 1].from || '');
								if (!isNaN(toVal) && !isNaN(nextFrom) && toVal >= nextFrom) {
									errors.push(
										this.createError({
											path: `zones.distances[${i}].to`,
											message: '"to" must be < next zone "from"',
										}),
									);
								}
							}
						});
						if (errors.length > 0) throw new Yup.ValidationError(errors);
						return true;
					}),
				modes: Yup.array().of(
					Yup.string()
						.required('Pricing mode is required')
						.oneOf(PRICING_MODES, 'Pricing mode is required'),
				),
				plnValues: Yup.array().of(
					Yup.array().of(positiveNumber(`${currency} value`)),
				),
			}),
		}),
	});

	const zoneIds = formik.values.zones.ids;
	const unitFor = (mode: PricingMode | '') =>
		mode === 'DAILY_RATE'
			? t('transportationFormula.unitDailyRate', '{currency} / day', {
					currency,
			  })
			: mode === 'PER_TON_KM'
			? t('transportationFormula.unitPerTonKm', '{currency} / t·km', {
					currency,
			  })
			: '—';

	const generalField = (
		name: 'margin' | 'workingHoursPerDay' | 'averageSpeedKmh',
		label: React.ReactNode,
		suffix: string,
	) => (
		<div className="w-full grid grid-cols-[180px,auto,48px] items-start gap-3">
			<SypacText variant="body-regular-medium">
				<p className="text-gray-80 mt-[10px]">{label}</p>
			</SypacText>
			{loading ? (
				<SkeletonInput />
			) : (
				<FormInput
					name={name}
					className="bg-[#EBECF2] rounded-[8px] transition hover:bg-white focus:bg-white"
					placeholder={t(
						'companyInformationEditable.enterValue',
						'Enter value',
					)}
					readOnly={!editFormula}
					formik={formik}
				/>
			)}
			<SypacText variant="body-regular-medium">
				<p className="text-gray-40 mt-[10px]">{suffix}</p>
			</SypacText>
		</div>
	);

	const rowLabel = (label: React.ReactNode) => (
		<div className="sticky left-0 z-10 bg-white min-w-[110px] pr-3 flex items-center">
			<SypacText variant="body-regular-medium">
				<p className="text-gray-80">{label}</p>
			</SypacText>
		</div>
	);

	return (
		<form onSubmit={formik.handleSubmit}>
			<main className="relative flex flex-col gap-5 w-[calc(100vw-67px)] xl-2xl:w-[calc(100vw-83px)] h-full pb-8">
				<header className="hidden xl:flex flex-col gap-4 mx-7.5">
					<div className="h-[44px] flex items-center gap-[24px]">
						<SypacText variant="heading-4">
							<p className="text-gray-80">
								<T keyName="transportationFormula.costFormulaParameters">
									Transportation formula cost formula parameters
								</T>
							</p>
						</SypacText>
						<div className="w-[1px] h-[12px] bg-gray-10" />
						<Sorting
							options={sortOptions}
							action={(item) => setSorting(item)}
							current={sorting}
							label={{ title: 'Country:', key: 'country' }}
							includeDefault={false}
						/>

						<div className="ml-auto">
							{!editFormula ? (
								<SypacButton variant="subTitle">
									<button
										type="button"
										className="w-fit p-0 m-0 flex justify-center items-center border-0"
										onClick={() => setEditFormula(true)}
									>
										<SypacText variant="body-regular-medium">
											<p className="text-cornflower-blue underline underline-offset-[3.5px] transition hover:text-cornflower-blue/80 hover:decoration-cornflower-blue/60">
												<T keyName="transportationFormula.editFormula">
													Edit transportation formula
												</T>
											</p>
										</SypacText>
									</button>
								</SypacButton>
							) : (
								<div className="w-full flex justify-center gap-4">
									<SypacButton variant="secondary" size="small">
										<button
											type="button"
											className="w-[248px] h-[44px] rounded-[8px]"
											onClick={() => {
												formik.resetForm();
												setEditFormula(false);
											}}
										>
											<SypacText variant="body-regular-medium">
												<p>
													<T keyName="transportationFormula.closeEdit">
														Close edit
													</T>
												</p>
											</SypacText>
										</button>
									</SypacButton>
									<SypacButton variant="primary" size="small">
										<button
											className="w-[248px] h-[44px] rounded-[8px]"
											type="submit"
											disabled={!formik.isValid || formik.isSubmitting}
										>
											<SypacText
												variant="body-regular-medium"
												className="flex items-center gap-4"
											>
												<p>
													<T keyName="transportationFormula.saveChanges">
														Save changes
													</T>
												</p>
											</SypacText>
										</button>
									</SypacButton>
								</div>
							)}
						</div>
					</div>
				</header>
				<div className="border-0 border-solid border-b-[1px] border-gray-10" />

				<section className="flex flex-col xl:flex-row gap-5 ml-5 mr-7.5 xl:mx-7.5">
					{/* General parameters */}
					<div className="w-full xl:w-[520px] flex flex-col gap-3 p-4 border border-solid border-gray-10 rounded-[10px]">
						<SypacText variant="body-regular-medium">
							<p className="text-gray-40">
								<T keyName="transportationFormula.generalParameters">
									General parameters
								</T>{' '}
								({sorting?.value})
							</p>
						</SypacText>
						{generalField(
							'margin',
							<T keyName="transportationFormula.margin">Margin</T>,
							'%',
						)}
						{generalField(
							'workingHoursPerDay',
							<T keyName="transportationFormula.workingHoursPerDay">
								Working hours per day
							</T>,
							'h',
						)}
						{generalField(
							'averageSpeedKmh',
							<T keyName="transportationFormula.averageSpeed">Average speed</T>,
							'km/h',
						)}
						<SypacText variant="body-regular-small">
							<p className="text-gray-40">
								<T keyName="transportationFormula.dailyRateHint">
									Daily-rate zones: courses per day = working hours × average
									speed ÷ (2 × distance); cost per ton = daily rate ÷ (tons ×
									courses per day).
								</T>
							</p>
						</SypacText>
					</div>

					{/* Weight categories */}
					<div className="w-full xl:w-[520px] flex flex-col gap-3 p-4 border border-solid border-gray-10 rounded-[10px]">
						<div className="w-full grid grid-cols-[116px,1fr,1fr] gap-[14px]">
							<SypacText variant="body-regular-medium">
								<p className="text-gray-40">
									<T keyName="transportationFormula.tons">tons</T>
								</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">
									<T keyName="transportationFormula.from">from</T>
								</p>
							</SypacText>
							<SypacText variant="body-regular-medium">
								<p className="text-gray-80">
									<T keyName="transportationFormula.to">to</T>
								</p>
							</SypacText>
						</div>
						{CATEGORY_NAMES.map((cat, idx) =>
							loading ? (
								<div
									key={cat}
									className="w-full grid grid-cols-[116px,auto] gap-3"
								>
									<SypacText variant="body-regular-medium">
										<p className="text-gray-80 mt-[10px]">Category {idx + 1}</p>
									</SypacText>
									<div className="flex gap-[14px]">
										<SkeletonInput />
										<SkeletonInput />
									</div>
								</div>
							) : (
								<CategoryRow
									key={cat}
									index={idx}
									name={cat}
									formik={formik}
									editFormula={editFormula}
								/>
							),
						)}
					</div>
				</section>

				{/* Zones × categories price matrix */}
				<section className="ml-5 mr-7.5 xl:mx-7.5 flex flex-col gap-3">
					<SypacText variant="body-regular-medium">
						<p className="text-gray-40">
							<T keyName="transportationFormula.zoneDistance">
								Zone distance (Loading to unloading place)
							</T>{' '}
							· km
						</p>
					</SypacText>
					<div className="w-full overflow-x-auto border border-solid border-gray-10 rounded-[10px] p-4">
						<div
							className="grid gap-x-[14px] gap-y-3 items-start"
							style={{
								gridTemplateColumns: `110px repeat(${Math.max(
									zoneIds.length,
									1,
								)}, minmax(104px, 1fr))`,
							}}
						>
							{/* Zone label + from */}
							{rowLabel(<T keyName="transportationFormula.fromFrom">From</T>)}
							{zoneIds.map((id, idx) =>
								loading ? (
									<SkeletonInput key={id} />
								) : (
									<ZoneHeader
										key={id}
										index={idx}
										label={`${t('transportationFormula.zone', 'Zone')} ${id}`}
										formik={formik}
										editFormula={editFormula}
									/>
								),
							)}

							{/* to */}
							{rowLabel(<T keyName="transportationFormula.toTo">To</T>)}
							{zoneIds.map((id, idx) =>
								loading ? (
									<SkeletonInput key={id} />
								) : (
									<FormInput
										key={id}
										name={`zones.distances[${idx}].to`}
										placeholder={
											idx === zoneIds.length - 1
												? '+'
												: t(
														'companyInformationEditable.enterValue',
														'Enter value',
												  )
										}
										readOnly={!editFormula}
										className={cn(
											'w-full bg-[#FAFAFA] rounded-[8px] transition hover:bg-white focus:bg-white',
											idx === zoneIds.length - 1 && 'placeholder:text-gray-80',
										)}
										formik={formik}
									/>
								),
							)}

							{/* pricing mode */}
							{rowLabel(
								<T keyName="transportationFormula.pricingMode">Pricing</T>,
							)}
							{zoneIds.map((id, idx) => {
								const mode = formik.values.zones.modes[idx];
								const modeError =
									// @ts-ignore
									formik.errors.zones?.modes?.[idx];
								return loading ? (
									<SkeletonInput key={id} />
								) : (
									<div key={id} className="flex flex-col gap-1">
										<select
											aria-label={`pricing-mode-${id}`}
											name={`zones.modes[${idx}]`}
											value={mode}
											disabled={!editFormula}
											onChange={(e) =>
												formik.setFieldValue(
													`zones.modes[${idx}]`,
													e.target.value,
												)
											}
											className={cn(
												'h-[42px] w-full px-2 rounded-[8px] border border-solid text-sm bg-[#FAFAFA]',
												modeError ? 'border-red' : 'border-gray-10',
											)}
										>
											<option value="" disabled>
												{t('transportationFormula.selectMode', 'Select')}
											</option>
											<option value="DAILY_RATE">
												{t('transportationFormula.modeDailyRate', 'Daily rate')}
											</option>
											<option value="PER_TON_KM">
												{t('transportationFormula.modePerTonKm', 'Per ton·km')}
											</option>
										</select>
										<SypacText variant="body-regular-small">
											<p
												className={cn(
													'text-center',
													modeError ? 'text-red' : 'text-gray-40',
												)}
											>
												{modeError || unitFor(mode)}
											</p>
										</SypacText>
									</div>
								);
							})}

							{/* price matrix rows */}
							{CATEGORY_NAMES.map((cat, rowIdx) => (
								<React.Fragment key={cat}>
									{rowLabel(
										<T
											keyName={`transportationFormula.${
												['categoryOne', 'categoryTwo', 'categoryThree'][rowIdx]
											}`}
										>
											{`Category ${rowIdx + 1}`}
										</T>,
									)}
									{zoneIds.map((id, colIdx) =>
										loading ? (
											<SkeletonInput key={id} />
										) : (
											<FormInput
												key={id}
												name={`zones.plnValues[${rowIdx}][${colIdx}]`}
												placeholder={t(
													'companyInformationEditable.enterValue',
													'Enter value',
												)}
												className="w-full bg-[#EBECF2] rounded-[8px] transition hover:bg-white focus:bg-white"
												formik={formik}
												readOnly={!editFormula}
											/>
										),
									)}
								</React.Fragment>
							))}
						</div>
					</div>
				</section>

				<CostPreview
					countryCode={sorting.value as 'PL' | 'MD'}
					currency={currency}
				/>
			</main>
		</form>
	);
};

export default TransportationFormula;
